/**
 * Tag color utilities
 * Provides consistent color assignment for tags across the application
 */

export interface TagColor {
  bg: string;
  text: string;
}

// Color palette for tags - cycles through these colors
export const TAG_COLORS: readonly TagColor[] = [
  { bg: '#DBEAFE', text: '#1E40AF' }, // blue
  { bg: '#DCFCE7', text: '#166534' }, // green
  { bg: '#FEF3C7', text: '#92400E' }, // amber
  { bg: '#FCE7F3', text: '#9D174D' }, // pink
  { bg: '#E0E7FF', text: '#3730A3' }, // indigo
  { bg: '#CCFBF1', text: '#115E59' }, // teal
  { bg: '#FEE2E2', text: '#991B1B' }, // red
  { bg: '#EDE9FE', text: '#5B21B6' }, // violet
  { bg: '#FFEDD5', text: '#9A3412' }, // orange
  { bg: '#E0F2FE', text: '#0369A1' }, // sky
  { bg: '#ECFCCB', text: '#3F6212' }, // lime
  { bg: '#F3E8FF', text: '#6B21A8' }, // purple
  { bg: '#CFFAFE', text: '#155E75' }, // cyan
  { bg: '#D1FAE5', text: '#065F46' }, // emerald
  { bg: '#FAE8FF', text: '#86198F' }, // fuchsia
  { bg: '#FFE4E6', text: '#9F1239' }, // rose
  { bg: '#F3F4F6', text: '#374151' }, // gray
] as const;

// Special tag colors
export const SPECIAL_TAG_COLORS: Record<string, TagColor> = {
  inbox: { bg: '#FEF3C7', text: '#92400E' }, // amber for inbox
};

/**
 * Simple hash function for string to get consistent color index
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Get a consistent color for a tag name.
 * Same tag name will always return the same color.
 *
 * @param tagName - The name of the tag
 * @returns TagColor object with bg and text colors
 *
 * @example
 * getTagColor('work') // => { bg: '#DBEAFE', text: '#1E40AF' }
 * getTagColor('inbox') // => { bg: '#FEF3C7', text: '#92400E' } (special color)
 */
export function getTagColor(tagName: string): TagColor {
  const normalizedName = tagName.toLowerCase().trim();

  // Check for special tags first
  const specialColor = SPECIAL_TAG_COLORS[normalizedName];
  if (specialColor) {
    return specialColor;
  }

  // Use hash to get consistent color
  const index = hashString(normalizedName) % TAG_COLORS.length;
  const color = TAG_COLORS[index];

  // TAG_COLORS is readonly and always has elements, so this is safe
  // But TypeScript needs explicit assertion
  if (!color) {
    return TAG_COLORS[0]!;
  }

  return color;
}

/**
 * Get CSS class names for a tag badge
 *
 * @param tagName - The name of the tag
 * @returns Object with backgroundColor and color styles
 */
export function getTagStyles(tagName: string): React.CSSProperties {
  const color = getTagColor(tagName);
  return {
    backgroundColor: color.bg,
    color: color.text,
  };
}

/**
 * Get all available tag colors (for color picker UI)
 */
export function getAllTagColors(): readonly TagColor[] {
  return TAG_COLORS;
}
